/**
 * Inter-unit transfers. MASTER_PROMPT sections 6.18 and decisions A7 to A10.
 *
 * A transfer has two legs and the screen shows both, because that is the
 * whole point: material leaves one unit, sits in transit, and is counted in at
 * the other. Anything short stays in transit rather than disappearing.
 *
 * The units hold different GSTINs (A8), so this is a taxable supply. The
 * screen surfaces the tax consequence at the moment the consignment is
 * created — an e-way bill is needed before the vehicle moves, not after
 * somebody notices.
 */

import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../lib/api";
import { api } from "../../lib/api";
import { ops, type UnitRow } from "../../lib/api2";
import { SheetHead, SheetToolbar } from "../../components/Sheet";
import { useSheet, type SheetColumn } from "../../lib/sheet";
import type { Item, TransferResult, TransitRow } from "../../lib/types";
import { fmtDate, fmtINR, fmtQty, today } from "../../lib/format";

const TRANSIT_COLUMNS: SheetColumn<TransitRow>[] = [
  { key: "doc_no", label: "Document", value: (r) => r.doc_no },
  { key: "dispatched", label: "Dispatched", type: "date",
    value: (r) => r.dispatched_on, format: (r) => fmtDate(r.dispatched_on) },
  { key: "age", label: "Age", type: "number", numeric: true,
    value: (r) => Number(r.age_days ?? 0) },
  { key: "bucket", label: "Bucket", value: (r) => r.bucket },
  { key: "vehicle", label: "Vehicle", value: (r) => r.vehicle_no ?? "" },
  { key: "value", label: "Value", type: "number", numeric: true,
    value: (r) => Number(r.total_value ?? 0),
    format: (r) => fmtINR(r.total_value) },
];

type Tab = "send" | "transit";

interface Line {
  key: string;
  itemCode: string;
  qty: string;
  rate: string;
}

const blank = (): Line => ({
  key: Math.random().toString(36).slice(2),
  itemCode: "",
  qty: "",
  rate: "",
});

export function Transfers() {
  const [tab, setTab] = useState<Tab>("transit");
  const [units, setUnits] = useState<UnitRow[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [banner, setBanner] = useState<{
    tone: "positive" | "negative" | "warning";
    text: string;
  } | null>(null);

  const [fromUnit, setFromUnit] = useState("");
  const [toUnit, setToUnit] = useState("");
  const [dispatchedOn, setDispatchedOn] = useState(() =>
    today()
  );
  const [vehicle, setVehicle] = useState("");
  const [lines, setLines] = useState<Line[]>([blank(), blank()]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<TransferResult | null>(null);

  const [transit, setTransit] = useState<TransitRow[] | null>(null);
  const [receiving, setReceiving] = useState<TransitRow | null>(null);
  const transitSheet = useSheet(transit ?? [], TRANSIT_COLUMNS);
  const [receivedOn, setReceivedOn] = useState(() =>
    today()
  );

  useEffect(() => {
    ops
      .units()
      .then((u) => {
        setUnits(u);
        if (u.length) setFromUnit(u[0].code);
        if (u.length > 1) setToUnit(u[1].code);
      })
      .catch(() => undefined);
    api.items().then(setItems).catch(() => undefined);
  }, []);

  const loadTransit = useCallback(() => {
    setTransit(null);
    ops
      .inTransit()
      .then(setTransit)
      .catch((e: ApiError) => setBanner({ tone: "negative", text: e.message }));
  }, []);

  useEffect(() => {
    if (tab === "transit") loadTransit();
  }, [tab, loadTransit]);

  function update(index: number, patch: Partial<Line>) {
    setLines((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      if (index === next.length - 1) next.push(blank());
      return next;
    });
  }

  async function send() {
    const payload = lines
      .filter((l) => l.itemCode && Number(l.qty) > 0)
      .map((l) => ({
        item_code: l.itemCode,
        qty: Number(l.qty),
        transfer_rate: l.rate ? Number(l.rate) : null,
      }));

    if (!payload.length) {
      setBanner({ tone: "warning", text: "Add at least one line." });
      return;
    }
    if (fromUnit === toUnit) {
      setBanner({ tone: "warning", text: "A transfer needs two different units." });
      return;
    }

    setBusy(true);
    setBanner(null);
    try {
      const created = await ops.createTransfer({
        from_unit_code: fromUnit,
        to_unit_code: toUnit,
        dispatched_on: dispatchedOn,
        vehicle_no: vehicle || null,
        lines: payload,
      });
      setResult(created);
      setLines([blank(), blank()]);
      setBanner({
        tone: created.warnings.length ? "warning" : "positive",
        text: `${created.doc_no} sent — ${fmtINR(created.total_value, { abbreviate: false })} including IGST.`,
      });
    } catch (err) {
      const e = err as ApiError;
      setBanner({ tone: "negative", text: e.blocked?.message ?? e.message });
    } finally {
      setBusy(false);
    }
  }

  async function receive(row: TransitRow, quantities?: Record<string, number>) {
    setBusy(true);
    try {
      const received = await ops.receiveTransfer(row.transfer_id, {
        received_on: receivedOn,
        quantities: quantities ?? null,
      });
      const short = Object.entries(received.shortages);
      setBanner(
        short.length
          ? {
              tone: "warning",
              text: `${row.doc_no} received short: ${short
                .map(([code, qty]) => `${code} ${fmtQty(qty, 3)}`)
                .join(", ")}. The shortage stays in transit until it is written off.`,
            }
          : {
              tone: "positive",
              text: `${row.doc_no} received in full after ${received.transit_days} day(s).`,
            }
      );
      setReceiving(null);
      loadTransit();
    } catch (err) {
      setBanner({ tone: "negative", text: (err as ApiError).message });
    } finally {
      setBusy(false);
    }
  }

  async function showEwayBill(row: TransitRow) {
    try {
      const payload = await ops.ewayBill(row.transfer_id);
      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `eway-${row.doc_no.replace(/\//g, "-")}.json`;
      link.click();
      URL.revokeObjectURL(url);
      setBanner({
        tone: "positive",
        text: "E-way bill payload downloaded. Upload it to the government portal — the system does not file on your behalf.",
      });
    } catch (err) {
      setBanner({ tone: "negative", text: (err as ApiError).message });
    }
  }

  const singleUnit = units.length < 2;

  return (
    <div className="page">
      <header className="page__head">
        <div>
          <h2 className="page__title">Inter-unit transfers</h2>
          <p className="page__sub">
            Two legs — out, in transit, then counted in at the other end
          </p>
        </div>
        <div className="page__controls">
          <div className="segmented" role="tablist">
            {(
              [
                ["transit", "In transit"],
                ["send", "Send"],
              ] as [Tab, string][]
            ).map(([id, label]) => (
              <button
                key={id}
                className={tab === id ? "is-active" : ""}
                onClick={() => setTab(id)}
                role="tab"
                aria-selected={tab === id}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {banner && (
        <div className={`banner banner--${banner.tone}`} role="alert">
          {banner.text}
        </div>
      )}

      {singleUnit && (
        <div className="banner banner--warning">
          Only one unit is configured. Add the other units in settings before
          transfers can be used — they are super-admin master data, and each
          needs its own GSTIN.
        </div>
      )}

      {tab === "send" && (
        <>
          <div className="page__controls" style={{ marginBottom: "var(--sp-4)" }}>
            <label className="inputgroup">
              <span className="label">From</span>
              <select
                value={fromUnit}
                onChange={(e) => setFromUnit(e.target.value)}
              >
                {units.map((u) => (
                  <option key={u.code} value={u.code}>
                    {u.name} · {u.state}
                  </option>
                ))}
              </select>
            </label>
            <label className="inputgroup">
              <span className="label">To</span>
              <select value={toUnit} onChange={(e) => setToUnit(e.target.value)}>
                <option value="">Choose…</option>
                {units.map((u) => (
                  <option key={u.code} value={u.code}>
                    {u.name} · {u.state}
                  </option>
                ))}
              </select>
            </label>
            <label className="inputgroup">
              <span className="label">Dispatched</span>
              <input
                type="date"
                value={dispatchedOn}
                onChange={(e) => setDispatchedOn(e.target.value)}
              />
            </label>
            <label className="inputgroup">
              <span className="label">Vehicle</span>
              <input
                value={vehicle}
                onChange={(e) => setVehicle(e.target.value.toUpperCase())}
                placeholder="BR01AB1234"
              />
            </label>
          </div>

          <div className="tablewrap">
            <table className="table table--entry">
              <thead>
                <tr>
                  <th>Item</th>
                  <th className="num">Quantity</th>
                  <th className="num">Transfer rate</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => (
                  <tr key={line.key}>
                    <td>
                      <select
                        value={line.itemCode}
                        onChange={(e) =>
                          update(index, { itemCode: e.target.value })
                        }
                      >
                        <option value="">Choose an item…</option>
                        {items.map((i) => (
                          <option key={i.code} value={i.code}>
                            {i.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        className="num mono"
                        inputMode="decimal"
                        value={line.qty}
                        placeholder="0"
                        onChange={(e) => update(index, { qty: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        className="num mono"
                        inputMode="decimal"
                        value={line.rate}
                        placeholder="cost + 10%"
                        onChange={(e) => update(index, { rate: e.target.value })}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="page__footer">
            <span className="entrytotals__sub">
              Left blank, the transfer price is cost plus 10% on a Rule 30
              basis. The units hold different GSTINs, so this is a taxable
              supply.
            </span>
            <button
              className="btn btn--auto"
              onClick={send}
              disabled={busy || singleUnit}
            >
              {busy ? <span className="spinner" /> : null}
              Send consignment
            </button>
          </div>

          {result && (
            <section className="card">
              <h3 className="section-title">{result.doc_no}</h3>
              <div className="kpirow" style={{ marginTop: "var(--sp-4)" }}>
                <div className="kpi">
                  <span className="label">Taxable value</span>
                  <span className="kpi-value num">
                    {fmtINR(result.taxable_value)}
                  </span>
                </div>
                <div className="kpi">
                  <span className="label">IGST</span>
                  <span className="kpi-value num">
                    {fmtINR(result.igst_amount)}
                  </span>
                </div>
                <div className="kpi">
                  <span className="label">Consignment value</span>
                  <span className="kpi-value num">
                    {fmtINR(result.total_value)}
                  </span>
                </div>
                <div className="kpi">
                  <span className="label">E-way bill</span>
                  <span
                    className={`kpi-value num${result.eway_bill_required ? " kpi-value--warn" : ""}`}
                  >
                    {result.eway_bill_required ? "Required" : "Not needed"}
                  </span>
                  <span className="kpi__sub">threshold ₹50,000</span>
                </div>
              </div>
              {result.warnings.length > 0 && (
                <div className="banner banner--warning">
                  <div>
                    {result.warnings.map((w) => (
                      <div key={w}>{w}</div>
                    ))}
                  </div>
                </div>
              )}
            </section>
          )}
        </>
      )}

      {tab === "transit" && (
        <>
          {!transit ? (
            <div className="state">
              {[0, 1, 2].map((i) => (
                <div key={i} className="skeleton state__bar" />
              ))}
            </div>
          ) : transit.length === 0 ? (
            <div className="state state--empty">
              <span className="state__glyph" aria-hidden="true">
                ✓
              </span>
              <h3>Nothing in transit</h3>
              <p>Every consignment sent has been counted in at the other end.</p>
            </div>
          ) : (
            <>
            <SheetToolbar sheet={transitSheet} exportName="goods-in-transit" />
            <div className="tablewrap">
              <table className="table">
                <SheetHead sheet={transitSheet} trailing={<th key="receive" />} />
                <tbody>
                  {transitSheet.rows.map((row) => (
                    <tr key={row.transfer_id} className={row.overdue ? "row--warn" : ""}>
                      <td className="mono">{row.doc_no}</td>
                      <td>{fmtDate(row.dispatched_on)}</td>
                      <td
                        className={`num mono${row.overdue ? " cell--negative" : ""}`}
                      >
                        {row.overdue ? "▲ " : ""}
                        {row.age_days}d
                      </td>
                      <td>{row.bucket}</td>
                      <td className="mono">{row.vehicle_no ?? "—"}</td>
                      <td className="num mono">{fmtINR(row.total_value)}</td>
                      <td>
                        <div style={{ display: "flex", gap: "var(--sp-2)" }}>
                          <button
                            className="btn btn--ghost btn--sm"
                            onClick={() => setReceiving(row)}
                          >
                            Receive
                          </button>
                          <button
                            className="btn btn--ghost btn--sm"
                            onClick={() => showEwayBill(row)}
                          >
                            E-way bill
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}

          {receiving && (
            <section className="card">
              <h3 className="section-title">Receive {receiving.doc_no}</h3>
              <p className="section-note">
                Counting it in full accepts what was sent. Anything short stays
                in transit until it is written off.
              </p>
              <div className="page__footer">
                <label className="inputgroup">
                  <span className="label">Received on</span>
                  <input
                    type="date"
                    value={receivedOn}
                    onChange={(e) => setReceivedOn(e.target.value)}
                  />
                </label>
                <button
                  className="btn btn--auto"
                  onClick={() => receive(receiving)}
                  disabled={busy}
                >
                  Receive in full
                </button>
                <button
                  className="btn btn--ghost btn--auto"
                  onClick={() => setReceiving(null)}
                >
                  Cancel
                </button>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
}
